import { withBase } from "@/lib/tenant-slug";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Menu, X, Download } from "lucide-react";
import htLogoFull from "@assets/HTCAPITAL_logo-01_trimmed.png";

interface LandingHeaderProps {
  onLoginClick: () => void;
  onRegisterClick: () => void;
}

export function LandingHeader({ onLoginClick, onRegisterClick }: LandingHeaderProps) {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { label: "Estrutura", anchor: "#estrutura" },
    { label: "Modelo Operacional", anchor: "#modelo-operacional" },
    { label: "Fases", anchor: "#fases" },
    { label: "Sobre", anchor: "#sobre" },
  ];

  const scrollToSection = (anchor: string) => {
    if (location !== "/") {
      window.location.href = withBase("/") + anchor;
      return;
    }
    const element = document.querySelector(anchor);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a1a]/95 backdrop-blur-md border-b border-white/5">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link href="/">
            <div className="flex items-center cursor-pointer">
              <img src={htLogoFull} alt="HT Capital" className="h-8 w-auto" data-testid="img-logo-header" />
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.anchor}
                onClick={() => scrollToSection(item.anchor)}
                className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {item.label}
              </button>
            ))}
            <a
              href="/api/download/hts"
              className="inline-flex items-center gap-2 text-sm font-bold text-white hover:text-primary transition-colors"
              data-testid="nav-baixar-plataforma"
            >
              <Download className="h-3.5 w-3.5" />
              BAIXAR PLATAFORMA
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={onLoginClick}
              className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
              data-testid="button-header-login"
            >
              Login
            </button>
            <Button
              size="sm"
              onClick={onRegisterClick}
              className="bg-primary hover:bg-primary/90 text-white text-sm px-4"
              data-testid="button-header-register"
            >
              Solicitar Avaliação
            </Button>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="button-mobile-menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-white/5 bg-[#0a0a1a]">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <button
                  key={item.anchor}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    scrollToSection(item.anchor);
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <a
                href="/api/download/hts"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full text-left px-3 py-2 text-sm font-bold text-white hover:text-primary transition-colors flex items-center gap-2"
              >
                <Download className="h-3.5 w-3.5" />
                BAIXAR PLATAFORMA
              </a>
              <div className="flex flex-col gap-2 pt-4 mt-2 border-t border-white/5">
                <button
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onLoginClick();
                  }}
                  className="w-full text-left px-3 py-2 text-sm font-medium text-gray-400 hover:text-white transition-colors"
                >
                  Login
                </button>
                <Button
                  className="bg-primary hover:bg-primary/90 text-white mx-3"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    onRegisterClick();
                  }}
                >
                  Solicitar Avaliação
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
